import { create } from 'zustand';
import { BusinessIdea } from '../services/businessIdeaTypes';
import { BusinessPlanSection, SectionId } from '../types/businessPlan';
import { anthropicService } from '../services/anthropicService';

// Define the GenerationContext interface
export interface GenerationContext {
  businessIdea: BusinessIdea;
  sections: Record<SectionId, BusinessPlanSection>;
  currentSection: SectionId;
  additionalData?: any; // Add this to accommodate the wizard data
}

export interface BusinessPlan {
  id: string;
  title: string;
  businessIdea: BusinessIdea;
  sections: Record<SectionId, BusinessPlanSection>;
  status: 'not-started' | 'generating' | 'completed' | 'error';
  createdAt: number;
  updatedAt: number;
  error?: string;
}

export interface BusinessPlanState {
  businessPlan: BusinessPlan | null;
  isGenerating: boolean;
  error: string | null;
  generateBusinessPlan: (idea: BusinessIdea) => Promise<void>;
  updateSection: (sectionId: SectionId, content: string) => void;
  clearError: () => void;
}

// Use shared singleton service to ensure consistent configuration and endpoints

// Helper function to create empty sections
const createEmptySectionsForStore = () => {
  return {} as Record<SectionId, BusinessPlanSection>;
};

export const useBusinessPlanStore = create<BusinessPlanState>((set, get) => ({
  businessPlan: null,
  isGenerating: false,
  error: null,

  generateBusinessPlan: async (idea: BusinessIdea) => {
    try {
      set({ isGenerating: true, error: null });

      const context: GenerationContext = { 
        businessIdea: idea, 
        sections: createEmptySectionsForStore(), 
        currentSection: 'executive-summary' 
      };
      const sections = await anthropicService.generateCompletePlan(context);

      const businessPlan: BusinessPlan = {
        id: Date.now().toString(),
        title: idea.title,
        businessIdea: idea,
        sections,
        status: 'completed',
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      set({
        businessPlan,
        isGenerating: false
      });
    } catch (error) {
      console.error('Error generating business plan:', error);
      set({
        error: 'Failed to generate business plan. Please try again.',
        isGenerating: false
      });
    }
  },

  updateSection: (sectionId: SectionId, content: string) => {
    const { businessPlan } = get();
    if (!businessPlan) return;

    const updatedSections = {
      ...businessPlan.sections,
      [sectionId]: {
        ...businessPlan.sections[sectionId],
        content,
        status: 'completed'
      }
    };

    set({
      businessPlan: {
        ...businessPlan,
        sections: updatedSections,
        updatedAt: Date.now()
      }
    });
  },

  clearError: () => set({ error: null })
}));
